import { useEffect, useRef } from 'react'
import { useChatStream, type ChatMessage } from '~/hooks/use-chat-stream'
import type { ChatProfile } from '~/api/types'
import { MessageBubble } from '~/components/chat/message-bubble'
import { ChatInput } from '~/components/chat/chat-input'
import { StreamingIndicator } from '~/components/chat/streaming-indicator'
import { CitationCard } from '~/components/chat/citation-card'
import { EmptyState } from '~/components/shared/empty-state'
import { Button } from '~/components/ui/button'
import { ErrorBanner } from '~/components/ui/error-banner'

export interface ChatPanelProps {
    profile: ChatProfile
    sessionId?: string
    businessContextId?: string
    /** 입력창 placeholder. */
    placeholder?: string
    /** 첫 진입 시 안내 문구. */
    emptyTitle?: string
    emptyDescription?: string
    /** 빠른 시작 칩. 클릭하면 해당 메시지를 바로 전송한다(스킬 시작 등). */
    quickPrompts?: { label: string; message: string }[]
    /** 저장된 세션을 열 때 시드할 초기 대화. */
    initialMessages?: ChatMessage[]
    /** 마운트 시 자동으로 한 번 전송할 메시지(예: 홈 HERO에서 넘어온 시드 질문). */
    autoSendMessage?: string
    /** done 이벤트로 sessionId가 처음 생기거나 바뀌면 호출된다(사이드바 갱신용). */
    onSessionId?(sessionId: string): void
}

/**
 * 대화 패널. use-chat-stream을 소비해 tool_call/ai_message/citation 이벤트를 렌더한다.
 * 메시지 영역과 근거 사이드를 함께 보여준다.
 */
export function ChatPanel({
    profile,
    sessionId,
    businessContextId,
    placeholder,
    emptyTitle = '무엇을 도와드릴까요?',
    emptyDescription,
    quickPrompts,
    initialMessages,
    autoSendMessage,
    onSessionId,
}: ChatPanelProps) {
    const {
        messages,
        citations,
        pendingChoices,
        toolActivity,
        isStreaming,
        error,
        stop,
        sendMessage,
        sessionId: streamSessionId,
    } = useChatStream({
        profile,
        sessionId,
        businessContextId,
        initialMessages,
        onSessionId,
    })

    const scrollRef = useRef<HTMLDivElement>(null)
    // 사용자가 스크롤을 올려 읽는 중이면 스트리밍 신규 내용이 스크롤을 하단으로 끌어내리지
    // 않도록 한다. 하단 근처(80px 이내)에 있을 때만 자동 추적한다.
    const stickToBottomRef = useRef(true)

    const handleScroll = () => {
        const el = scrollRef.current
        if (!el) return
        const distanceFromBottom =
            el.scrollHeight - el.scrollTop - el.clientHeight
        stickToBottomRef.current = distanceFromBottom <= 80
    }

    // 시드 질문 자동 전송 — 한 번만(ref 가드). 기존 sendMessage 경로 재사용.
    // StrictMode(dev) 이중 마운트에서 첫 fetch가 가짜 unmount의 abort로 죽고
    // 가드 때문에 재전송이 막히는 문제를 피하려, 다음 틱에 전송한다(이중 마운트가
    // 끝난 뒤 fetch가 시작돼 abort되지 않음). 가드는 실제 전송 시점에 설정한다.
    const autoSentRef = useRef(false)
    useEffect(() => {
        const seed = autoSendMessage?.trim()
        if (!seed || autoSentRef.current) return
        const timer = setTimeout(() => {
            autoSentRef.current = true
            sendMessage(seed)
        }, 0)
        return () => clearTimeout(timer)
    }, [autoSendMessage, sendMessage])

    // 세션이 바뀌면(새 상담·다른 내역 열기) 다시 하단 추적을 켠다 — 새로 연 대화는 하단부터 본다.
    useEffect(() => {
        stickToBottomRef.current = true
    }, [sessionId])

    useEffect(() => {
        if (!stickToBottomRef.current) return
        scrollRef.current?.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: 'smooth',
        })
    }, [messages, toolActivity])

    // 선택형 HITL — 도구가 후보(candidates)를 주면 그것을, 없어도 마지막 답변이
    // 예/아니오 질문이면 예/아니오/모름 칩을 제공한다(상담·자가진단 등 전 흐름 복원).
    const lastAssistant = [...messages]
        .reverse()
        .find((m) => m.role === 'assistant' && !m.streaming)
    const isYesNo = /예\s*\/\s*아니[오요]/.test(lastAssistant?.content ?? '')
    const choices =
        pendingChoices.length > 0
            ? pendingChoices
            : isYesNo
              ? ['예', '아니오', '모름/해당없음']
              : []

    return (
        <div className='grid h-full gap-4 lg:grid-cols-[1fr_320px]'>
            <div className='border-border bg-background flex h-full min-h-0 flex-col rounded-md border'>
                <div
                    ref={scrollRef}
                    onScroll={handleScroll}
                    className='flex-1 space-y-3 overflow-y-auto p-4'>
                    {messages.length === 0 ? (
                        <EmptyState
                            title={emptyTitle}
                            description={emptyDescription}
                        />
                    ) : (
                        messages.map((m) => (
                            <MessageBubble
                                key={m.id}
                                message={m}
                                feedbackService={
                                    profile === 'consultation'
                                        ? 'consultation'
                                        : undefined
                                }
                                feedbackTargetId={streamSessionId}
                            />
                        ))
                    )}

                    {isStreaming ? (
                        <StreamingIndicator toolActivity={toolActivity} />
                    ) : null}

                    {error ? <ErrorBanner>{error}</ErrorBanner> : null}
                </div>

                <div className='border-border border-t p-3'>
                    {isStreaming ? (
                        <div className='mb-2 flex justify-end'>
                            <Button
                                variant='secondary'
                                size='sm'
                                onClick={stop}>
                                중지
                            </Button>
                        </div>
                    ) : null}
                    {/* 선택형 HITL — 도구가 준 후보를 클릭하면 그 값으로 바로 답한다(한 줄씩). */}
                    {!isStreaming && choices.length > 0 ? (
                        <ul className='mb-2 grid gap-1.5' aria-label='선택지'>
                            {choices.map((choice) => (
                                <li key={choice}>
                                    <button
                                        type='button'
                                        onClick={() => void sendMessage(choice)}
                                        className='border-primary/40 text-primary hover:bg-accent bg-card flex w-full items-center justify-between gap-2 rounded-lg border px-3.5 py-2.5 text-left text-sm font-medium transition-colors'>
                                        <span>{choice}</span>
                                        <span aria-hidden className='text-xs'>
                                            ›
                                        </span>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                    {quickPrompts && quickPrompts.length > 0 ? (
                        <ul
                            className='mb-2 flex flex-wrap gap-1.5'
                            aria-label='빠른 시작 스킬'>
                            {quickPrompts.map((q) => (
                                <li key={q.label}>
                                    <button
                                        type='button'
                                        onClick={() =>
                                            void sendMessage(q.message)
                                        }
                                        disabled={isStreaming}
                                        className='border-border bg-card text-muted-foreground hover:border-primary hover:text-primary rounded border px-2.5 py-1 text-xs transition-colors disabled:opacity-50'>
                                        {q.label}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : null}
                    <ChatInput
                        onSend={sendMessage}
                        disabled={isStreaming}
                        placeholder={placeholder}
                    />
                </div>
            </div>

            <aside className='hidden min-h-0 flex-col gap-3 overflow-y-auto lg:flex'>
                <h2 className='text-foreground text-sm font-semibold'>
                    근거 자료
                </h2>
                {citations.length === 0 ? (
                    <p className='text-muted-foreground text-sm'>
                        답변에 사용된 근거가 여기에 표시돼요.
                    </p>
                ) : (
                    citations.map((c, i) => (
                        <CitationCard
                            key={`${c.documentId}-${i}`}
                            citation={c}
                            index={i}
                        />
                    ))
                )}
            </aside>
        </div>
    )
}
